<template>
  <div class="content safeInfo" :style="{ '--level-color': levelColor }">
    <div class="infoBar">
      <span class="infoBarTitle">实训室安全信息</span>
    </div>

    <div v-if="!infoId" class="emptyTip">缺少参数：id</div>
    <div v-else-if="loaded && isEmpty" class="emptyTip">暂无数据</div>

    <template v-else-if="loaded">
      <div class="mainCard">
        <div class="mainCardTop">
          <span class="mainCardName">{{ roomInfo.training_name }}</span>
          <span class="levelBadge">{{ roomInfo.level_desc }}</span>
        </div>
        <div class="mainCardSub">
          {{ roomInfo.training_no }} · {{ roomInfo.safety_category }} · {{ roomInfo.room_name }}
        </div>
        <div class="mainCardTime">当前安全状态已于 {{ roomInfo.updated_at }} 更新</div>
      </div>

      <div class="section">
        <div class="sectionTitle">当前安全要点</div>
        <div class="fieldRow">
          <span class="fieldLabel">危险源</span>
          <span class="fieldValue pointValue">{{ keyPoints.hazard_ids_desc }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">风险点</span>
          <span class="fieldValue pointValue">{{ keyPoints.risk_ids_desc }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">灭火要点</span>
          <span class="fieldValue pointValue">{{ keyPoints.extinguish }}</span>
        </div>
      </div>

      <div class="section">
        <div class="sectionTitle">
          <span>最近一次安全风险评分</span>
          <span class="detailLink" v-if="lastRating.id" @click="openDetail('rating', lastRating.id)">查看评分详情 <i
              class="el-icon-arrow-right"></i></span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">评分时间</span>
          <span class="fieldValue">{{ lastRating.created_at }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">评分人</span>
          <span class="fieldValue">{{ lastRating.create_name }}</span>
        </div>
        <!-- <div class="fieldRow">
          <span class="fieldLabel">原始分 · 最终分</span>
          <span class="fieldValue pending">待对接</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">规则版本</span>
          <span class="fieldValue pending">待对接</span>
        </div> -->
        <div class="fieldRow">
          <span class="fieldLabel">分数</span>
          <span class="fieldValue"><span class="scoreBadge">{{ lastRating.total_score }}</span></span>
        </div>
      </div>

      <div class="section">
        <div class="sectionTitle">
          <span>最近一次安全巡查</span>
          <span class="detailLink" v-if="lastCheck.id" @click="openDetail('check', lastCheck.id)">查看详情 <i
              class="el-icon-arrow-right"></i></span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">巡查时间</span>
          <span class="fieldValue">{{ lastCheck.created_at }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">巡查人</span>
          <span class="fieldValue">{{ lastCheck.creator_name }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">巡查内容</span>
          <span class="fieldValue">{{ lastCheck.content }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">上报结果</span>
          <span class="fieldValue">
            <el-tag size="mini" :type="resultTagType(lastCheck.check_result)">{{ lastCheck.check_result }}</el-tag>
          </span>
        </div>
      </div>

      <div class="section">
        <div class="sectionTitle">
          <span>最近一次专项检查</span>
          <span class="detailLink" v-if="lastSpecial.id" @click="openDetail('special', lastSpecial.id)">查看详情 <i
              class="el-icon-arrow-right"></i></span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">检查时间</span>
          <span class="fieldValue">{{ lastSpecial.created_at }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">检查人</span>
          <span class="fieldValue">{{ lastSpecial.creator_name }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">检查内容</span>
          <span class="fieldValue">{{ lastSpecial.standard_desc }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">上报结果</span>
          <span class="fieldValue">
            <el-tag size="mini" :type="resultTagType(lastSpecial.standard_res)">{{ lastSpecial.standard_res }}</el-tag>
            <span class="countText">合格数 {{ lastSpecial.pass_count }} / 不合格数 {{ lastSpecial.fail_count }}</span>
          </span>
        </div>
      </div>

      <div class="section">
        <div class="sectionTitle">
          <span>最近一次安全自查</span>
          <span class="detailLink" v-if="lastSelf.id" @click="openDetail('self', lastSelf.id)">查看详情 <i
              class="el-icon-arrow-right"></i></span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">自查时间</span>
          <span class="fieldValue">{{ lastSelf.created_at }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">自查人</span>
          <span class="fieldValue">{{ lastSelf.creator_name }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">检查内容</span>
          <span class="fieldValue">{{ lastSelf.standard_desc }}</span>
        </div>
        <div class="fieldRow">
          <span class="fieldLabel">上报结果</span>
          <span class="fieldValue">
            <el-tag size="mini" :type="resultTagType(lastSelf.standard_res)">{{ lastSelf.standard_res }}</el-tag>
            <span class="countText">合格数 {{ lastSelf.pass_count }} / 不合格数 {{ lastSelf.fail_count }}</span>
          </span>
        </div>
      </div>

      <div class="section" v-if="hazardList.length">
        <div class="sectionTitle">
          <span>未整改隐患</span>
          <span class="sectionTitleRight">
            <span class="sectionCount" v-if="hazardList.length > 1">{{ hazardList.length }} 条</span>
            <span class="detailLink" v-if="hazardList.length === 1 && hazardList[0].id"
              @click="openDetail('hazard', hazardList[0].id)">查看详情 <i class="el-icon-arrow-right"></i></span>
          </span>
        </div>
        <div class="hazardGroup" v-for="(item, index) in hazardList" :key="index">
          <div class="fieldRow">
            <span class="fieldLabel">隐患描述</span>
            <span class="fieldValue">
              {{ item.description }}
              <span class="detailLink" v-if="hazardList.length > 1 && item.id"
                @click="openDetail('hazard', item.id)">查看详情</span>
            </span>
          </div>
          <div class="fieldRow">
            <span class="fieldLabel">隐患等级</span>
            <span class="fieldValue">{{ hazardLevelText(item.hazard_level) }}</span>
          </div>
          <div class="fieldRow">
            <span class="fieldLabel">责任人</span>
            <span class="fieldValue">{{ item.checker_name }}</span>
          </div>
          <div class="fieldRow">
            <span class="fieldLabel">整改期限</span>
            <span class="fieldValue">{{ item.rectification_limit_date }}</span>
          </div>
          <div class="fieldRow">
            <span class="fieldLabel">整改状态</span>
            <span class="fieldValue">
              <el-tag size="mini" type="warning">{{ item.status }}</el-tag>
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- 详情弹窗：按接口返回字段铺开 -->
    <el-dialog :close-on-click-modal="false" :title="detailTitle" :visible.sync="detailVisible" width="900px">
      <!-- 评分详情：明细列表 -->
      <template v-if="detailType === 'rating'">
        <div class="detailFields">
          <div class="fieldRow"><span class="fieldLabel">评分人</span><span class="fieldValue">{{ lastRating.create_name
              }}</span></div>
          <div class="fieldRow"><span class="fieldLabel">评分时间</span><span class="fieldValue">{{ lastRating.created_at
              }}</span></div>
          <div class="fieldRow"><span class="fieldLabel">总分</span><span class="fieldValue">{{ lastRating.total_score
              }}</span></div>
        </div>
        <custom-table :data="ratingItems" border style="width: 100%">
          <custom-table-column fixed label="序号" type="index" width="50"></custom-table-column>
          <custom-table-column prop="category" label="评分栏目" min-width="200"></custom-table-column>
          <custom-table-column prop="score" label="分值" width="90"></custom-table-column>
          <custom-table-column label="是否符合" width="110">
            <template slot-scope="scope">{{ scope.row.conform == 1 ? '符合' : '不符合' }}</template>
          </custom-table-column>
          <custom-table-column prop="remark" label="备注" min-width="200"></custom-table-column>
        </custom-table>
      </template>

      <template v-else>
        <div class="detailFields">
          <div class="fieldRow" v-for="item in detailFields" :key="item.label">
            <span class="fieldLabel">{{ item.label }}</span>
            <span class="fieldValue">{{ item.value }}</span>
          </div>
          <div class="fieldRow" v-if="detailType === 'hazard' && detailData.image">
            <span class="fieldLabel">隐患图片</span>
            <span class="fieldValue">
              <el-image style="width:120px;height:120px;" :src="detailData.image"
                :preview-src-list="[detailData.image]">
              </el-image>
            </span>
          </div>
        </div>

        <!-- 巡查详情：整改隐患 -->
        <template v-if="detailType === 'check'">
          <div class="detailSubTitle">整改隐患（{{ (detailData.hazards || []).length }}）</div>
          <custom-table :data="detailData.hazards || []" border style="width: 100%">
            <custom-table-column fixed label="序号" type="index" width="50"></custom-table-column>
            <custom-table-column prop="description" label="隐患描述" min-width="180"></custom-table-column>
            <custom-table-column prop="hazard_level_text" label="隐患等级" width="100"></custom-table-column>
            <custom-table-column prop="checker_name" label="责任人" width="120"></custom-table-column>
            <custom-table-column prop="rectification_limit_date" label="整改期限" width="160"></custom-table-column>
            <custom-table-column prop="rectification_status_text" label="整改状态" width="110"></custom-table-column>
          </custom-table>
        </template>

        <!-- 专项检查详情：检查项 -->
        <template v-if="detailType === 'special'">
          <div class="detailSubTitle">检查项（{{ (detailData.standards || []).length }}）</div>
          <custom-table :data="detailData.standards || []" border style="width: 100%">
            <custom-table-column fixed label="序号" type="index" width="50"></custom-table-column>
            <custom-table-column prop="name" label="检查项" min-width="220"></custom-table-column>
            <custom-table-column prop="result_text" label="检查结果" width="120"></custom-table-column>
            <custom-table-column prop="target_name" label="不合格实验室" min-width="160"></custom-table-column>
          </custom-table>
        </template>

        <!-- 安全自查详情：上报明细 -->
        <template v-if="detailType === 'self'">
          <div class="detailSubTitle">上报明细（{{ (detailData.reports || []).length }}）</div>
          <custom-table :data="detailData.reports || []" border style="width: 100%">
            <custom-table-column fixed label="序号" type="index" width="50"></custom-table-column>
            <custom-table-column prop="standard_name" label="自查项" min-width="220"></custom-table-column>
            <custom-table-column prop="standard_res" label="自查结果" width="120"></custom-table-column>
          </custom-table>
        </template>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import CustomTable from '@/components/CustomTable';
import CustomTableColumn from '@/components/CustomTableColumn';

export default {
  name: 'trainingSafeInfo',
  // 本页不用 mixinList（没有列表/分页），表格组件需自行注册
  components: {
    CustomTable,
    CustomTableColumn,
  },
  data() {
    return {
      infoId: '',
      // 安全级别配色
      levelColors: {
        'Ⅰ级': '#DC2626',
        'Ⅱ级': '#EA580C',
        'Ⅲ级': '#F3AA11',
        'Ⅳ级': '#2563EB',
      },
      loaded: false,
      roomInfo: {},
      keyPoints: {},
      lastRating: {},
      lastCheck: {},
      lastSpecial: {},
      lastSelf: {},
      hazardList: [],
      detailVisible: false,
      detailType: '',
      detailData: {},
      ratingItems: [],
      detailTitleMap: {
        rating: '评分详情',
        check: '安全巡查详情',
        special: '专项检查详情',
        self: '安全自查详情',
        hazard: '隐患整改详情',
      },
      detailUrlMap: {
        check: 'api/safe-check-activity/show/',
        special: 'api/special-check-activity/show/',
        self: 'api/self-check-activity/show/',
        hazard: 'api/hazard-rectification/show/',
      },
      detailFieldMap: {
        check: [
          ['name', '巡查名称'],
          ['check_date', '巡查日期'],
          ['target_name', '巡查对象'],
          ['content', '巡查内容'],
          ['status_text', '巡查状态'],
          ['creator_name', '创建人'],
          ['submit_name', '上报人'],
          ['submit_time', '上报时间'],
          ['check_result_text', '上报结果'],
        ],
        special: [
          ['name', '检查名称'],
          ['check_date', '检查日期'],
          ['target_name', '检查对象'],
          ['status_text', '检查状态'],
          ['desc', '说明'],
        ],
        self: [
          ['name', '自查名称'],
          ['start_time', '开始时间'],
          ['end_time', '结束时间'],
          ['target_name', '自查对象'],
          ['creator_name', '创建人'],
          ['desc', '说明'],
        ],
        hazard: [
          ['activity_name', '来源'],
          ['target_name', '隐患对象'],
          ['checker_name', '责任人'],
          ['check_date', '检查日期'],
          ['description', '隐患描述'],
          ['hazard_level', '隐患等级', 'yhlevel'],
          ['rectification_limit_date', '整改期限'],
          ['rectification_status', '整改状态', 'zgStatus'],
          ['review_comment', '审核意见'],
          ['rectification_desc', '整改说明'],
        ],
      },
    };
  },
  computed: {
    // 接口按 title 返回区块，未返回的区块视为无数据
    isEmpty() {
      return !this.roomInfo.title && !this.keyPoints.title && !this.hazardList.length;
    },
    // 卡片配色跟随实训室级别(level_desc 形如"Ⅱ级/橙色级实验室")
    levelColor() {
      let desc = this.roomInfo.level_desc || '';
      let hit = Object.keys(this.levelColors).find((key) => desc.indexOf(key) > -1);
      return hit ? this.levelColors[hit] : '#6B7280';
    },
    detailTitle() {
      return this.detailTitleMap[this.detailType] || '详情';
    },
    detailFields() {
      let config = this.detailFieldMap[this.detailType] || [];
      // 配置第三项为 store 字典名时按字典取文本（详情接口没返回 *_text）
      return config.map((item) => ({
        label: item[1],
        value: item[2]
          ? this.$until.getLabelName(this.$store.state[item[2]], 'value', 'label', this.detailData[item[0]])
          : this.detailData[item[0]],
      }));
    },
  },
  watch: {
    // 同一路由只改 query 时组件会复用,mounted 不再触发,需要手动重查
    '$route.query.id'(val) {
      this.infoId = val || '';
      if (this.infoId) {
        this.loaded = false;
        this.getInfo();
      }
    },
  },
  mounted() {
    this.infoId = this.$route.query.id || '';
    if (this.infoId) {
      this.getInfo();
    }
  },
  methods: {
    handleBack() {
      this.$router.back();
    },
    async openDetail(type, id) {
      this.detailType = type;
      this.detailData = {};
      this.ratingItems = [];
      this.detailVisible = true;
      if (type === 'rating') {
        let res = await this.$api.instance.interfaceServer(`api/rating/detail/${id}`, 'get');
        if (res.code == 0) {
          this.ratingItems = res.data.data || [];
        }
        return;
      }
      let res = await this.$api.instance.interfaceServer(this.detailUrlMap[type] + id, 'get');
      if (res.code == 0) {
        this.detailData = res.data || {};
      }
    },
    async getInfo() {
      let res = await this.$api.instance.interfaceServer(`api/hazards/safeCardDetail/${this.infoId}`, 'get');
      if (res.code == 0) {
        let list = res.data || [];
        this.roomInfo = list.find((item) => item.title === '实训室信息') || {};
        this.keyPoints = list.find((item) => item.title === '当前安全要点') || {};
        this.lastRating = list.find((item) => item.title === '最近一次安全风险评分') || {};
        this.lastCheck = list.find((item) => item.title === '最近一次安全巡查') || {};
        this.lastSpecial = list.find((item) => item.title === '最近一次专项检查') || {};
        this.lastSelf = list.find((item) => item.title === '最近一次安全自查') || {};
        // 未整改隐患可能存在多条，逐条收集
        this.hazardList = list.filter((item) => item.title === '未整改隐患');
      }
      this.loaded = true;
    },
    hazardLevelText(level) {
      return this.$until.getLabelName(this.$store.state.yhlevel, 'value', 'label', level);
    },
    // 接口返回的是中文结果文本，按文本给标签配色
    resultTagType(text) {
      if (!text || text.indexOf('未检查') > -1) return 'info';
      if (text.indexOf('部分') > -1) return 'warning';
      if (text.indexOf('全部合格') > -1 || text.indexOf('无隐患') > -1) return 'success';
      return 'danger';
    },
  },
};
</script>

<style lang="less" scoped>
.content {
  margin-top: 0px;
  background: #F3F6FA;
}

.safeInfo {
  .infoBar {
    text-align: center;
    height: 36px;
    margin-top: 12px;
    .backBtn {
      font-size: 14px;
      color: #606266;
      cursor: pointer;

      &:hover {
        color: #1d64d8;
      }
    }

    .infoBarTitle {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .infoBarHolder {
      width: 56px;
    }
  }

  .emptyTip {
    padding: 60px 0;
    text-align: center;
    font-size: 14px;
    color: #909399;
  }

  .mainCard {
    padding: 18px 24px;
    border-radius: 4px;
    background-color: var(--level-color);
    background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.14), rgba(0, 0, 0, 0.14));
    color: #fff;

    .mainCardTop {
      display: flex;
      align-items: center;

      .mainCardName {
        font-size: 16px;
        font-weight: 600;
        flex:1;
      }

      .levelBadge {
        margin-left: 12px;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 12px;
        background: rgba(255, 255, 255, 0.9);
        color: var(--level-color);
        width: 70px;
        text-align: center;
      }
    }

    .mainCardSub {
      margin-top: 10px;
      font-size: 13px;
      opacity: 0.9;
    }

    .mainCardTime {
      margin-top: 6px;
      font-size: 12px;
      opacity: 0.75;
    }
  }

  .section {
    margin-top: 12px;
    padding: 14px 12px 6px;
    border-radius: 4px;
    background: #fff;

    .sectionTitle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 10px;
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      border-bottom: 1px solid #f0f2f5;
    }

    .sectionCount {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }
  }

  .fieldRow {
    display: flex;
    padding: 9px 0;
    font-size: 13px;
    line-height: 20px;

    .fieldLabel {
      flex: 0 0 80px;
      color: #909399;
    }

    .fieldValue {
      flex: 1;
      color: #303133;
      word-break: break-all;
    }

    .pointValue {
      color: var(--level-color);
    }

    .pending {
      color: #c0c4cc;
    }

    .countText {
      margin-left: 10px;
      font-size: 12px;
      color: #909399;
    }

    .scoreBadge {
      font-weight: 600;
      color: #e5453f;
    }
  }

  .detailLink {
    font-size: 13px;
    font-weight: 400;
    color: #1d64d8;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  .detailFields {
    padding-bottom: 6px;
  }

  .detailSubTitle {
    padding: 10px 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }

  .sectionTitleRight {
    display: flex;
    align-items: center;

    .sectionCount {
      margin-right: 12px;
    }
  }

  .hazardGroup+.hazardGroup {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f0f2f5;
  }
}
</style>
